import React, { useState, useEffect } from 'react';
import { loginCustomer, registerCustomer } from '../../../utils/supabase';
import { getStoredReferralCode, clearStoredReferralCode } from '../../../utils/supabaseReferral';
import { useContent } from '../../../context/ContentContext';
import { resolveMediaUrl } from '../../../utils/api';
import { CustomerProfile } from '../../../types';
import {
  Phone,
  User,
  Calendar,
  Lock,
  Eye,
  EyeOff,
  AlertCircle,
  CheckCircle2,
  Loader2,
  ArrowRight,
  ArrowLeft,
  Gift,
  MapPin,
  Coffee,
} from 'lucide-react';

interface CustomerAuthFormProps {
  onAuthSuccess: (profile: CustomerProfile) => void;
  initialMode?: 'login' | 'register';
  onBackToChoice?: () => void;
  onSkipWithoutMember?: () => void;
}

export default function CustomerAuthForm({
  onAuthSuccess,
  initialMode = 'login',
  onBackToChoice,
  onSkipWithoutMember,
}: CustomerAuthFormProps) {
  const { data } = useContent();
  const siteSettings = data?.siteSettings;
  const cmsLogoSrc = siteSettings?.logoUrl
    ? resolveMediaUrl(siteSettings.logoUrl)
    : '/logo_icon.jpg';
  const [logoImgError, setLogoImgError] = useState(false);

  useEffect(() => {
    setLogoImgError(false);
  }, [siteSettings?.logoUrl]);

  const [isRegisterMode, setIsRegisterMode] = useState(initialMode === 'register');
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [showForgotModal, setShowForgotModal] = useState(false);

  // Password visibility states
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showRegPassword, setShowRegPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Sync mode if initialMode prop changes
  useEffect(() => {
    setIsRegisterMode(initialMode === 'register');
  }, [initialMode]);

  // Register Form States
  const [namaLengkap, setNamaLengkap] = useState('');
  const [nomorHp, setNomorHp] = useState('');
  const [tanggalLahir, setTanggalLahir] = useState('');
  const [alamat, setAlamat] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [referralCode, setReferralCode] = useState(() => getStoredReferralCode());

  // Login Form States (Nomor HP & Password)
  const [loginPhone, setLoginPhone] = useState('');
  const [loginPassword, setLoginPassword] = useState('');

  const clearAlerts = () => {
    if (errorMsg) setErrorMsg('');
    if (successMsg) setSuccessMsg('');
  };

  const toggleMode = () => {
    setIsRegisterMode(!isRegisterMode);
    setErrorMsg('');
    setSuccessMsg('');
    setNamaLengkap('');
    setNomorHp('');
    setTanggalLahir('');
    setAlamat('');
    setRegPassword('');
    setConfirmPassword('');
    setReferralCode(getStoredReferralCode());
    setLoginPhone('');
    setLoginPassword('');
    setShowLoginPassword(false);
    setShowRegPassword(false);
    setShowConfirmPassword(false);
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    clearAlerts();

    const cleanInput = loginPhone.trim();
    if (!cleanInput) {
      setErrorMsg('Nomor WhatsApp wajib diisi.');
      return;
    }
    if (!loginPassword) {
      setErrorMsg('Password wajib diisi.');
      return;
    }

    setLoading(true);
    try {
      const res = await loginCustomer(cleanInput, loginPassword);
      if (res.success && res.profile) {
        setSuccessMsg('Masuk berhasil! Membuka akun member...');
        onAuthSuccess(res.profile);
      } else {
        setErrorMsg(res.error || 'Nomor WhatsApp atau password salah.');
      }
    } catch (err: any) {
      console.error('[CustomerAuthForm handleLogin exception]:', err);
      setErrorMsg(err?.message || 'Nomor WhatsApp atau password salah.');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    clearAlerts();

    const cleanNama = namaLengkap.trim();
    if (!cleanNama) {
      setErrorMsg('Nama Lengkap wajib diisi.');
      return;
    }
    if (cleanNama.length < 2) {
      setErrorMsg('Nama Lengkap minimal 2 karakter.');
      return;
    }

    const cleanHp = nomorHp.replace(/[^0-9]/g, '');
    if (!cleanHp) {
      setErrorMsg('Nomor WhatsApp wajib diisi.');
      return;
    }
    if (cleanHp.length < 9) {
      setErrorMsg('Nomor WhatsApp tidak valid (minimal 9 digit angka).');
      return;
    }

    if (!tanggalLahir) {
      setErrorMsg('Tanggal Lahir wajib diisi.');
      return;
    }
    const birthDateObj = new Date(tanggalLahir);
    const today = new Date();
    if (birthDateObj >= today) {
      setErrorMsg('Tanggal Lahir tidak valid (tidak bisa di masa depan).');
      return;
    }

    const cleanAlamat = alamat.trim();
    if (!cleanAlamat) {
      setErrorMsg('Alamat rumah wajib diisi.');
      return;
    }
    if (cleanAlamat.length < 5) {
      setErrorMsg('Alamat rumah minimal 5 karakter.');
      return;
    }

    if (!regPassword) {
      setErrorMsg('Password wajib diisi.');
      return;
    }
    if (regPassword.length < 6) {
      setErrorMsg('Password harus minimal 6 karakter.');
      return;
    }

    if (regPassword !== confirmPassword) {
      setErrorMsg('Password dan konfirmasi password tidak cocok.');
      return;
    }

    setLoading(true);
    try {
      const res = await registerCustomer(
        cleanNama,
        cleanHp,
        tanggalLahir,
        regPassword,
        referralCode.trim(),
        cleanAlamat
      );
      if (res.success && res.profile) {
        clearStoredReferralCode();
        setSuccessMsg('Pendaftaran berhasil! Membuka akun member...');
        onAuthSuccess(res.profile);
      } else {
        setErrorMsg(res.error || 'Pendaftaran gagal. Silakan periksa kembali data Anda.');
      }
    } catch (err: any) {
      console.error('[CustomerAuthForm handleRegister exception]:', err);
      setErrorMsg(err?.message || 'Terjadi kesalahan sistem saat mendaftar.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div id="customer-auth-container" className="w-full min-h-screen sm:min-h-0 sm:max-w-md sm:mx-auto sm:my-8 sm:px-0 sm:py-0 bg-white sm:bg-transparent">
      <div className="w-full bg-white sm:rounded-[2rem] sm:shadow-[0_20px_50px_rgba(0,86,210,0.12)] sm:border sm:border-slate-100 overflow-hidden">
        {/* Top Blue Gradient Header with Brand Logo & Wave/Cloud Transition */}
        <div className="relative bg-gradient-to-br from-[#0066FF] via-[#0052CC] to-[#003A99] text-white pt-6 pb-10 px-6 text-center overflow-hidden">
          {/* Decorative Ambient Circles */}
          <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full bg-white/10 blur-2xl pointer-events-none" />
          <div className="absolute top-10 -left-12 w-36 h-36 rounded-full bg-sky-300/15 blur-2xl pointer-events-none" />
          <div className="absolute bottom-6 right-1/4 w-24 h-24 rounded-full bg-white/5 blur-xl pointer-events-none" />

          {/* Optional Back / Skip Top Bar */}
          {(onBackToChoice || onSkipWithoutMember) && (
            <div className="relative z-10 flex items-center justify-between mb-3">
              {onBackToChoice ? (
                <button
                  type="button"
                  onClick={onBackToChoice}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/15 hover:bg-white/25 backdrop-blur-xs text-xs font-semibold text-white transition-all cursor-pointer"
                >
                  <ArrowLeft className="w-3.5 h-3.5" />
                  <span>Kembali</span>
                </button>
              ) : (
                <div />
              )}

              {onSkipWithoutMember && (
                <button
                  type="button"
                  onClick={onSkipWithoutMember}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white/15 hover:bg-white/25 backdrop-blur-xs text-xs font-semibold text-white transition-all cursor-pointer"
                >
                  <span>Tanpa Member</span>
                  <ArrowRight className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          )}

          {/* Brand Logo & LetonKu Name */}
          <div className="relative z-10 flex flex-col items-center">
            <div className="w-20 h-20 rounded-full bg-white/15 backdrop-blur-md p-1.5 shadow-lg border border-white/25 mb-3 flex items-center justify-center">
              <div className="w-full h-full rounded-full bg-white flex items-center justify-center overflow-hidden shadow-inner">
                {!logoImgError ? (
                  <img
                    src={cmsLogoSrc}
                    alt={siteSettings?.brandName || 'LetonKu Logo'}
                    decoding="async"
                    referrerPolicy="no-referrer"
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      const target = e.currentTarget;
                      if (!target.src.endsWith('/logo_icon.jpg')) {
                        target.src = '/logo_icon.jpg';
                      } else {
                        setLogoImgError(true);
                      }
                    }}
                  />
                ) : (
                  <Coffee className="w-8 h-8 text-[#0052CC]" />
                )}
              </div>
            </div>

            <p className="text-xs font-medium text-blue-100 tracking-wide">
              Selamat Datang di
            </p>
            <h1 className="text-2xl font-extrabold tracking-tight text-white mt-0.5">
              LetonKu
            </h1>
          </div>

          {/* White Wave / Cloud Transition at Bottom of Header */}
          <div className="absolute bottom-0 left-0 right-0 w-full overflow-hidden leading-none pointer-events-none">
            <svg
              className="relative block w-full h-10 sm:h-12"
              viewBox="0 0 1440 160"
              preserveAspectRatio="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M0,64 C240,140 480,0 720,64 C960,128 1200,20 1440,80 L1440,160 L0,160 Z"
                fill="rgba(255,255,255,0.22)"
              />
              <path
                d="M0,96 C320,20 640,148 960,72 C1180,20 1320,88 1440,64 L1440,160 L0,160 Z"
                fill="rgba(255,255,255,0.45)"
              />
              <path
                d="M0,112 C360,168 720,32 1080,112 C1260,148 1360,96 1440,112 L1440,160 L0,160 Z"
                fill="#FFFFFF"
              />
            </svg>
          </div>
        </div>

        {/* White Form Body */}
        <div className="px-6 sm:px-8 pt-3 pb-8 bg-white">
          {/* Section Title */}
          <div className="text-center mb-5">
            <h2 className="text-xl font-bold text-[#0F172A] tracking-tight">
              {isRegisterMode ? 'Daftar Member Baru' : 'Masuk ke Akun'}
            </h2>
            <p className="text-xs text-slate-500 mt-1">
              {isRegisterMode
                ? 'Lengkapi data diri Anda untuk menikmati benefit member LetonKu'
                : 'Silakan masuk menggunakan nomor WhatsApp dan password Anda'}
            </p>
          </div>

          {/* Alert Error */}
          {errorMsg && (
            <div
              id="customer-auth-error"
              className="mb-5 p-3.5 bg-red-50 border border-red-200 rounded-2xl flex items-start gap-2.5 text-red-700 text-xs animate-fadeIn"
            >
              <AlertCircle className="w-4 h-4 shrink-0 text-red-500 mt-0.5" />
              <span className="font-medium leading-normal">{errorMsg}</span>
            </div>
          )}

          {/* Alert Success */}
          {successMsg && (
            <div
              id="customer-auth-success"
              className="mb-5 p-3.5 bg-emerald-50 border border-emerald-200 rounded-2xl flex items-start gap-2.5 text-emerald-800 text-xs animate-fadeIn"
            >
              <CheckCircle2 className="w-4 h-4 shrink-0 text-emerald-500 mt-0.5" />
              <span className="font-medium leading-normal">{successMsg}</span>
            </div>
          )}

          {isRegisterMode ? (
            /* Form Pendaftaran Member */
            <form onSubmit={handleRegister} className="space-y-4">
              <div>
                <label
                  htmlFor="input-reg-nama"
                  className="block text-xs font-semibold text-slate-700 mb-1.5"
                >
                  Nama Lengkap
                </label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 flex items-center pl-4 pointer-events-none text-slate-400">
                    <User className="w-4 h-4" />
                  </span>
                    <input
                      id="input-reg-nama"
                      type="text"
                      required
                      disabled={loading}
                      placeholder="Masukkan nama lengkap"
                      value={namaLengkap}
                      onChange={(e) => {
                        clearAlerts();
                        setNamaLengkap(e.target.value);
                      }}
                      className="w-full pl-11 pr-4 py-3 bg-[#F8FAFC] border border-slate-200 rounded-2xl text-base sm:text-sm text-[#0F172A] placeholder:text-slate-400 focus:outline-none focus:bg-white focus:ring-2 focus:ring-[#0056D2]/20 focus:border-[#0056D2] disabled:opacity-60 transition-all"
                    />
                </div>
              </div>

              <div>
                <label
                  htmlFor="input-reg-phone"
                  className="block text-xs font-semibold text-slate-700 mb-1.5"
                >
                  Nomor WhatsApp
                </label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 flex items-center pl-4 pointer-events-none text-slate-400">
                    <Phone className="w-4 h-4" />
                  </span>
                    <input
                      id="input-reg-phone"
                      type="tel"
                      inputMode="numeric"
                      required
                      disabled={loading}
                      placeholder="Contoh: 081234567890"
                      value={nomorHp}
                      onChange={(e) => {
                        clearAlerts();
                        setNomorHp(e.target.value.replace(/[^0-9]/g, ''));
                      }}
                      className="w-full pl-11 pr-4 py-3 bg-[#F8FAFC] border border-slate-200 rounded-2xl text-base sm:text-sm text-[#0F172A] placeholder:text-slate-400 focus:outline-none focus:bg-white focus:ring-2 focus:ring-[#0056D2]/20 focus:border-[#0056D2] disabled:opacity-60 transition-all"
                    />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                <div>
                  <label
                    htmlFor="input-reg-birthdate"
                    className="block text-xs font-semibold text-slate-700 mb-1.5"
                  >
                    Tanggal Lahir
                  </label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 flex items-center pl-4 pointer-events-none text-slate-400">
                      <Calendar className="w-4 h-4" />
                    </span>
                    <input
                      id="input-reg-birthdate"
                      type="date"
                      required
                      disabled={loading}
                      value={tanggalLahir}
                      onChange={(e) => {
                        clearAlerts();
                        setTanggalLahir(e.target.value);
                      }}
                      className="w-full pl-11 pr-3 py-3 bg-[#F8FAFC] border border-slate-200 rounded-2xl text-base sm:text-xs text-[#0F172A] focus:outline-none focus:bg-white focus:ring-2 focus:ring-[#0056D2]/20 focus:border-[#0056D2] disabled:opacity-60 transition-all"
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="input-reg-referral-code"
                    className="block text-xs font-semibold text-slate-700 mb-1.5"
                  >
                    Kode Referral <span className="text-slate-400 font-normal">(opsional)</span>
                  </label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 flex items-center pl-4 pointer-events-none text-slate-400">
                      <Gift className="w-4 h-4 text-[#0056D2]" />
                    </span>
                    <input
                      id="input-reg-referral-code"
                      type="text"
                      disabled={loading}
                      placeholder="LET892ABC"
                      value={referralCode}
                      onChange={(e) => {
                        clearAlerts();
                        setReferralCode(e.target.value.toUpperCase());
                      }}
                      className="w-full pl-11 pr-3 py-3 bg-[#F8FAFC] border border-slate-200 rounded-2xl text-base sm:text-xs font-mono uppercase text-[#0F172A] placeholder:text-slate-400 focus:outline-none focus:bg-white focus:ring-2 focus:ring-[#0056D2]/20 focus:border-[#0056D2] disabled:opacity-60 transition-all"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label
                  htmlFor="input-reg-alamat"
                  className="block text-xs font-semibold text-slate-700 mb-1.5"
                >
                  Alamat Rumah
                </label>
                <div className="relative">
                  <span className="absolute top-3.5 left-0 flex items-center pl-4 pointer-events-none text-slate-400">
                    <MapPin className="w-4 h-4 text-[#0056D2]" />
                  </span>
                  <textarea
                    id="input-reg-alamat"
                    required
                    rows={2}
                    disabled={loading}
                    placeholder="Contoh: Jl. Merdeka No. 12 (Min. 5 karakter)"
                    value={alamat}
                    onChange={(e) => {
                      clearAlerts();
                      setAlamat(e.target.value);
                    }}
                    className="w-full pl-11 pr-4 py-2.5 bg-[#F8FAFC] border border-slate-200 rounded-2xl text-base sm:text-sm text-[#0F172A] placeholder:text-slate-400 focus:outline-none focus:bg-white focus:ring-2 focus:ring-[#0056D2]/20 focus:border-[#0056D2] disabled:opacity-60 transition-all resize-none"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                <div>
                  <label
                    htmlFor="input-reg-password"
                    className="block text-xs font-semibold text-slate-700 mb-1.5"
                  >
                    Password <span className="text-slate-400 font-normal">(Min. 6)</span>
                  </label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 flex items-center pl-4 pointer-events-none text-slate-400">
                      <Lock className="w-4 h-4" />
                    </span>
                    <input
                      id="input-reg-password"
                      type={showRegPassword ? 'text' : 'password'}
                      required
                      disabled={loading}
                      placeholder="••••••"
                      value={regPassword}
                      onChange={(e) => {
                        clearAlerts();
                        setRegPassword(e.target.value);
                      }}
                      className="w-full pl-11 pr-10 py-3 bg-[#F8FAFC] border border-slate-200 rounded-2xl text-base sm:text-sm text-[#0F172A] placeholder:text-slate-400 focus:outline-none focus:bg-white focus:ring-2 focus:ring-[#0056D2]/20 focus:border-[#0056D2] disabled:opacity-60 transition-all"
                    />
                    <button
                      type="button"
                      tabIndex={-1}
                      onClick={() => setShowRegPassword(!showRegPassword)}
                      className="absolute inset-y-0 right-0 flex items-center pr-3.5 text-slate-400 hover:text-slate-600 transition-colors cursor-pointer"
                      aria-label={showRegPassword ? 'Sembunyikan password' : 'Tampilkan password'}
                    >
                      {showRegPassword ? (
                        <EyeOff className="w-4 h-4" />
                      ) : (
                        <Eye className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="input-reg-confirm-password"
                    className="block text-xs font-semibold text-slate-700 mb-1.5"
                  >
                    Konfirmasi Password
                  </label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 flex items-center pl-4 pointer-events-none text-slate-400">
                      <Lock className="w-4 h-4" />
                    </span>
                    <input
                      id="input-reg-confirm-password"
                      type={showConfirmPassword ? 'text' : 'password'}
                      required
                      disabled={loading}
                      placeholder="••••••"
                      value={confirmPassword}
                      onChange={(e) => {
                        clearAlerts();
                        setConfirmPassword(e.target.value);
                      }}
                      className="w-full pl-11 pr-10 py-3 bg-[#F8FAFC] border border-slate-200 rounded-2xl text-base sm:text-sm text-[#0F172A] placeholder:text-slate-400 focus:outline-none focus:bg-white focus:ring-2 focus:ring-[#0056D2]/20 focus:border-[#0056D2] disabled:opacity-60 transition-all"
                    />
                    <button
                      type="button"
                      tabIndex={-1}
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute inset-y-0 right-0 flex items-center pr-3.5 text-slate-400 hover:text-slate-600 transition-colors cursor-pointer"
                      aria-label={
                        showConfirmPassword ? 'Sembunyikan password' : 'Tampilkan password'
                      }
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="w-4 h-4" />
                      ) : (
                        <Eye className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>
              </div>

              <div className="pt-2">
                <button
                  id="btn-submit-register"
                  type="submit"
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-[#0066FF] via-[#0052CC] to-[#003D99] hover:from-[#005CE6] hover:to-[#003380] disabled:from-slate-200 disabled:to-slate-200 disabled:text-slate-400 text-white font-bold py-3.5 px-6 rounded-full text-sm transition-all shadow-lg shadow-blue-600/25 hover:shadow-blue-600/35 active:scale-[0.99] focus:outline-none cursor-pointer disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Mendaftarkan Member...</span>
                    </>
                  ) : (
                    <span>Daftar Sekarang</span>
                  )}
                </button>
              </div>
            </form>
          ) : (
            /* Form Login Pelanggan (Sesuai Referensi leton_coffee_mobile_login 2.html) */
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label
                  htmlFor="input-login-phone"
                  className="block text-xs font-semibold text-slate-700 mb-1.5"
                >
                  Nomor WhatsApp
                </label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 flex items-center pl-4 pointer-events-none text-slate-400">
                    <Phone className="w-4 h-4" />
                  </span>
                  <input
                    id="input-login-phone"
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    required
                    disabled={loading}
                    placeholder="Contoh: 081234567890"
                    value={loginPhone}
                    onChange={(e) => {
                      clearAlerts();
                      setLoginPhone(e.target.value);
                    }}
                    className="w-full pl-11 pr-4 py-3.5 bg-[#F8FAFC] border border-slate-200 rounded-2xl text-base sm:text-sm text-[#0F172A] placeholder:text-slate-400 focus:outline-none focus:bg-white focus:ring-2 focus:ring-[#0056D2]/20 focus:border-[#0056D2] disabled:opacity-60 transition-all"
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="input-login-password"
                  className="block text-xs font-semibold text-slate-700 mb-1.5"
                >
                  Password
                </label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 flex items-center pl-4 pointer-events-none text-slate-400">
                    <Lock className="w-4 h-4" />
                  </span>
                  <input
                    id="input-login-password"
                    type={showLoginPassword ? 'text' : 'password'}
                    required
                    disabled={loading}
                    placeholder="Masukkan password Anda"
                    value={loginPassword}
                    onChange={(e) => {
                      clearAlerts();
                      setLoginPassword(e.target.value);
                    }}
                    className="w-full pl-11 pr-11 py-3.5 bg-[#F8FAFC] border border-slate-200 rounded-2xl text-base sm:text-sm text-[#0F172A] placeholder:text-slate-400 focus:outline-none focus:bg-white focus:ring-2 focus:ring-[#0056D2]/20 focus:border-[#0056D2] disabled:opacity-60 transition-all"
                  />
                  <button
                    type="button"
                    tabIndex={-1}
                    onClick={() => setShowLoginPassword(!showLoginPassword)}
                    className="absolute inset-y-0 right-0 flex items-center pr-4 text-slate-400 hover:text-slate-600 transition-colors cursor-pointer"
                    aria-label={showLoginPassword ? 'Sembunyikan password' : 'Tampilkan password'}
                  >
                    {showLoginPassword ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </button>
                </div>

                {/* Tautan Lupa Password */}
                <div className="flex justify-end mt-2">
                  <button
                    type="button"
                    onClick={() => setShowForgotModal(true)}
                    className="text-xs font-semibold text-[#0056D2] hover:text-blue-700 hover:underline cursor-pointer transition-colors"
                  >
                    Lupa Password?
                  </button>
                </div>
              </div>

              {/* Tombol Masuk Berbentuk Pill dengan Gradient Biru */}
              <div className="pt-2">
                <button
                  id="btn-submit-login"
                  type="submit"
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-[#0066FF] via-[#0052CC] to-[#003D99] hover:from-[#005CE6] hover:to-[#003380] disabled:from-slate-200 disabled:to-slate-200 disabled:text-slate-400 text-white font-bold py-3.5 px-6 rounded-full text-sm transition-all shadow-lg shadow-blue-600/25 hover:shadow-blue-600/35 active:scale-[0.99] focus:outline-none cursor-pointer disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Memproses Masuk...</span>
                    </>
                  ) : (
                    <span>Masuk</span>
                  )}
                </button>
              </div>
            </form>
          )}

          {/* Footer Tautan Daftar Sekarang / Masuk & Lanjut Tanpa Member */}
          <div className="mt-6 pt-4 border-t border-slate-100 text-center space-y-3">
            <p className="text-xs text-slate-500">
              {isRegisterMode ? 'Sudah punya akun member?' : 'Belum punya akun?'}
              <button
                id="btn-toggle-auth-mode"
                type="button"
                disabled={loading}
                onClick={toggleMode}
                className="ml-1.5 font-bold text-[#0056D2] hover:text-blue-700 hover:underline cursor-pointer disabled:opacity-50 transition-colors"
              >
                {isRegisterMode ? 'Masuk Sekarang' : 'Daftar Sekarang'}
              </button>
            </p>

            {onSkipWithoutMember && (
              <button
                type="button"
                disabled={loading}
                onClick={onSkipWithoutMember}
                className="w-full py-2.5 px-4 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold transition-all cursor-pointer flex items-center justify-center gap-1.5"
              >
                <span>Lanjut Tanpa Member</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            )}

            <p className="text-[10px] text-slate-400 pt-1 leading-normal">
              Dengan melanjutkan, Anda menyetujui{' '}
              <a
                href="/privacy-policy"
                onClick={(e) => {
                  e.preventDefault();
                  try {
                    window.history.pushState(null, '', '/privacy-policy');
                    window.dispatchEvent(new PopStateEvent('popstate'));
                  } catch {
                    window.location.href = '/privacy-policy';
                  }
                }}
                className="text-[#0056D2] hover:underline font-semibold"
              >
                Kebijakan Privasi
              </a>{' '}
              LetonKu.
            </p>
          </div>
        </div>
      </div>

      {/* Forgot Password Modal */}
      {showForgotModal && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-center justify-center p-4 animate-fadeIn">
          <div className="bg-white rounded-3xl max-w-sm w-full p-6 shadow-2xl border border-slate-200 text-center space-y-4">
            <div className="w-12 h-12 rounded-full bg-blue-50 text-[#0056D2] flex items-center justify-center mx-auto">
              <Lock className="w-6 h-6" />
            </div>
            <h4 className="font-display font-black text-lg text-[#0A192F]">
              Bantuan Lupa Password
            </h4>
            <p className="text-xs text-slate-600 leading-relaxed">
              Untuk keamanan akun member, pemulihan password dapat dilakukan dengan menghubungi
              admin outlet atau official WhatsApp LetonKu untuk verifikasi nomor WhatsApp
              Anda.
            </p>
            <div className="pt-2 flex gap-2">
              <a
                href="https://wa.me/6281234567890?text=Halo%20Admin%20Leton%20Coffee,%20saya%20lupa%20password%20akun%20member%20saya."
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-2.5 px-4 rounded-full text-xs uppercase tracking-wider transition-all"
              >
                Chat WhatsApp Admin
              </a>
              <button
                type="button"
                onClick={() => setShowForgotModal(false)}
                className="px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-full text-xs transition-all cursor-pointer"
              >
                Tutup
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
